import { Field, Image, Link, Stack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { UnauthLayout } from "layouts/UnauthLayout";
import { useBrand } from "brand/BrandConfig";
import { CoreAPIError } from "services/coreapi";
import { useAuthStore } from "stores/AuthStoreProvider";
import { useNotificationStore } from "stores/NotificationStoreProvider";
import {
  defaultLoginUI,
  FORM_GAP,
  FormError,
  LoginNotice,
  LoginUI,
} from "./loginUI";
import {
  checkNewPassword,
  describePasswordRefusal,
  passwordMatch,
} from "./passwordRules";
import { isOfflineError, OFFLINE_MESSAGE } from "./useLoginForm";

const SAVE_FAILED = "Couldn't save your password. Try again.";
const MAYBE_OTHER_PASSWORD =
  'It may not be the one you just typed. If it doesn\'t work at login, use "Forgot password?".';

/** Where `RouteGuard` parks a user with no password. After a save the guard,
 *  not this page, sends them on to `?redirect=`. */
export const SetPasswordPage = observer(
  ({
    ui: uiOverrides,
  }: {
    /** App-specific look, same as `<LoginPage ui={…} />`. */
    ui?: Partial<LoginUI>;
  }) => {
    const brand = useBrand();
    const authStore = useAuthStore();
    const notificationStore = useNotificationStore();

    const ui = React.useMemo<LoginUI>(
      () => ({ ...defaultLoginUI, ...uiOverrides }),
      [uiOverrides],
    );

    const [password, setPassword] = React.useState<string>("");
    const [repeat, setRepeat] = React.useState<string>("");
    const [hasLeftRepeat, setHasLeftRepeat] = React.useState<boolean>(false);
    // One eye for both boxes: either one shows or hides both.
    const [isRevealed, setIsRevealed] = React.useState<boolean>(false);
    const toggleReveal = () => setIsRevealed((v) => !v);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    // State updates land late: a ref stops a double click sending two requests.
    const isSaving = React.useRef(false);
    // A failed try may still have saved. "Already set" is surely this
    // password only when every earlier try sent it too.
    const sentPasswords = React.useRef(new Set<string>());

    async function save(): Promise<void> {
      if (isSaving.current) {
        return;
      }
      const problem = checkNewPassword(password, repeat);
      setError(problem);
      if (problem) {
        return;
      }

      const onlyThisOneSent =
        sentPasswords.current.size === 1 && sentPasswords.current.has(password);
      sentPasswords.current.add(password);

      isSaving.current = true;
      setIsLoading(true);
      try {
        const result = await authStore.setPassword(password);
        if (result === "already-set" && !onlyThisOneSent) {
          // A toast, as the guard moves the user on right away.
          notificationStore.show({
            type: "info",
            title: "Your account already had a password",
            description: MAYBE_OTHER_PASSWORD,
            duration: 15000,
            closable: true,
          });
        }
      } catch (err) {
        console.error(err);
        const code = err instanceof CoreAPIError ? err.code : undefined;
        const status = err instanceof CoreAPIError ? err.status : undefined;
        const refusal = describePasswordRefusal(code);
        if (status === 401) {
          // A 401 can be a blip. A session that is really gone drops the user,
          // and the guard shows login; otherwise it was just a failed save.
          await authStore.revalidateSession();
          setError(SAVE_FAILED);
        } else if (refusal) {
          setError(refusal);
        } else {
          setError(isOfflineError(err) ? OFFLINE_MESSAGE : SAVE_FAILED);
        }
      } finally {
        isSaving.current = false;
        setIsLoading(false);
      }
    }

    return (
      <UnauthLayout>
        <Image
          src={brand.logo}
          alt={`${brand.name} logo`}
          width={"120px"}
          mx="auto"
          mb={6}
        />

        <Stack
          width="full"
          as="form"
          gap={FORM_GAP}
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <LoginNotice title="Set a password">
            Next time, log in with your email and this password.
          </LoginNotice>

          {/* A field per box: inputs in one shared field all get its id. */}
          <Field.Root>
            {/* Read-only, and first in the form: a password manager saves the
                new password under this address. */}
            <ui.Input
              type="email"
              name="email"
              autoComplete="username"
              aria-label="Email"
              readOnly
              value={authStore.user?.data.email ?? ""}
              // A long address must not look like a different, shorter one.
              textOverflow="ellipsis"
            />
            <Field.HelperText
              width="100%"
              textAlign="center"
              fontSize="xs"
              color="whiteAlpha.500"
            >
              To change email,{" "}
              <Link
                href={`mailto:${brand.email}`}
                color="whiteAlpha.700"
                textDecoration="underline"
                _hover={{ color: "white" }}
              >
                contact support
              </Link>
              .
            </Field.HelperText>
          </Field.Root>

          <Field.Root>
            <ui.Input
              type="password"
              name="new-password"
              autoComplete="new-password"
              aria-label="Password"
              placeholder="Password"
              isRevealed={isRevealed}
              onToggleReveal={toggleReveal}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
            />
          </Field.Root>

          <Field.Root>
            <ui.Input
              type="password"
              name="repeat-password"
              autoComplete="new-password"
              aria-label="Repeat password"
              placeholder="Repeat password"
              isRevealed={isRevealed}
              onToggleReveal={toggleReveal}
              match={passwordMatch(password, repeat, hasLeftRepeat)}
              value={repeat}
              onChange={(e) => {
                setRepeat(e.target.value);
                setError(null);
              }}
              onBlur={() => setHasLeftRepeat(true)}
            />
          </Field.Root>

          <FormError isValid={true} error={error} />

          <ui.SubmitButton isLoading={isLoading} disabled={isLoading}>
            Save
          </ui.SubmitButton>
        </Stack>
      </UnauthLayout>
    );
  },
);
